import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { Fragment } from 'react';
import { PiDownloadSimple } from 'react-icons/pi';
import { Link } from 'wouter';
import { PayerKycBadge } from '@/components/PayerKycBadge';
import { TSplitPart, useDownloadInvoicePDF } from '@/models/invoices';
import { usePayers } from '@/models/payers';
import {
  type TSplitLine,
  type TSplitPlanItem,
  useIssueInstallment,
} from '@/models/split-plan';
import { InstallmentContents } from './payments/InstallmentContents';

const STATUS_COLOR: Record<string, string> = {
  issued: 'blue',
  'confirmed-by-client': 'cyan',
  'partially-paid': 'yellow',
  paid: 'green',
  cancelled: 'gray',
};

const fmtMoney = (n: number, ccy: string) =>
  `${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

export function PartsList({
  parts,
  offerId,
  accountId,
  currencyTotals,
  planItems = [],
  selectedLines = [],
  issuableSeq = null,
  canIssue = false,
  canReadPayers = true,
  recipientBlockReason,
}: {
  parts: TSplitPart[];
  offerId?: number;
  accountId?: number;
  currencyTotals?: Record<string, { paid: number; outstanding: number }> | null;
  /** Supplied on a split offer so every installment — issued or not — gets a row. */
  planItems?: TSplitPlanItem[];
  selectedLines?: TSplitLine[];
  /** The one installment that may be issued right now, if any. */
  issuableSeq?: number | null;
  canIssue?: boolean;
  /**
   * Operations hold no payers:read, so the payer column is dropped for them rather
   * than rendered from a request that would only 403. The offer header already names
   * the paying party, so nothing is actually lost.
   */
  canReadPayers?: boolean;
  recipientBlockReason?: string | null;
}) {
  const { data: payers = [] } = usePayers(accountId, undefined, canReadPayers);

  const payerName = (id: number | null) =>
    id == null
      ? '—'
      : (payers.find((p) => p.id === id)?.displayName ?? `#${id}`);
  const payerOf = (id: number | null) =>
    id == null ? null : (payers.find((p) => p.id === id) ?? null);

  const interactive = offerId != null;

  const downloadPDF = useDownloadInvoicePDF();

  const issue = useIssueInstallment(offerId ?? 0);

  // An installment is the unit; its invoice is a property it may not have yet.
  // Parts no plan item claims (a cancelled original, a manual invoice) still get a
  // row of their own — a schedule that silently drops an invoice is worse than one
  // with an odd row in it.
  const rows = [
    ...[...planItems]
      .sort((a, b) => a.seq - b.seq)
      .map((item) => ({
        key: `i${item.id}`,
        item,
        part: parts.find((p) => p.id === item.invoiceId) ?? null,
      })),
    ...parts
      .filter((p) => !planItems.some((it) => it.invoiceId === p.id))
      .map((p) => ({ key: `p${p.id}`, item: null, part: p })),
  ];

  return (
    <Stack gap="xs">
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Installment</Table.Th>
            {canReadPayers && <Table.Th>Payer</Table.Th>}
            <Table.Th>Due</Table.Th>
            <Table.Th>Amount</Table.Th>
            <Table.Th>Status</Table.Th>
            {interactive && <Table.Th w={40} />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map(({ key, item, part }) => {
            const amount = part
              ? fmtMoney(part.dueAmount, part.currency)
              : fmtMoney(Number(item?.amount ?? 0), item?.currency ?? '');
            const isIssuable =
              item != null && item.seq === issuableSeq && !recipientBlockReason;
            return (
              <Fragment key={key}>
                <Table.Tr>
                  <Table.Td>
                    {/* Once an installment has an invoice, its row is the way through
                        to that invoice. Accounting reached the offer first and had no
                        route onward but the invoices list and a search by number. */}
                    <Stack gap={0}>
                      <Text fz="sm">
                        {item
                          ? `Installment ${item.seq}`
                          : part?.partNumber == null
                            ? 'Full'
                            : `Part ${part.partNumber}`}
                      </Text>
                      {part && (
                        <Anchor
                          component={Link}
                          to={`~/invoices/${part.id}`}
                          fz="xs"
                        >
                          {part.invoiceNumber ?? `Invoice #${part.id}`}
                        </Anchor>
                      )}
                    </Stack>
                  </Table.Td>
                  {canReadPayers && (
                    <Table.Td>
                      {part ? (
                        <Group gap={6} wrap="nowrap">
                          <Text fz="sm">{payerName(part.payerId)}</Text>
                          {part.payerId != null && (
                            <PayerKycBadge payer={payerOf(part.payerId)} />
                          )}
                        </Group>
                      ) : (
                        <Text fz="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                  )}
                  <Table.Td>
                    {part?.dueAt ? dayjs(part.dueAt).format('DD.MM.YYYY') : '—'}
                  </Table.Td>
                  <Table.Td>{amount}</Table.Td>
                  <Table.Td>
                    {part ? (
                      <Badge
                        variant="light"
                        color={STATUS_COLOR[part.status] ?? 'gray'}
                      >
                        {part.status}
                      </Badge>
                    ) : item?.fulfilled ? (
                      <Badge variant="light" color="green">
                        fulfilled
                      </Badge>
                    ) : (
                      <Badge variant="light" color="gray">
                        {item && item.coveredLines.length > 0
                          ? 'awaiting delivery'
                          : 'awaiting coverage'}
                      </Badge>
                    )}
                  </Table.Td>
                  {interactive && (
                    <Table.Td>
                      {part ? (
                        <Tooltip label="Download invoice">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            loading={
                              downloadPDF.isPending &&
                              downloadPDF.variables === String(part.id)
                            }
                            onClick={() => downloadPDF.mutate(String(part.id))}
                          >
                            <PiDownloadSimple size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        item != null &&
                        canIssue && (
                          <Tooltip
                            label={
                              recipientBlockReason ??
                              'Previous installment must be fully paid'
                            }
                            disabled={isIssuable}
                            multiline
                            w={260}
                          >
                            <span>
                              <Button
                                size="xs"
                                variant="light"
                                disabled={!isIssuable}
                                loading={
                                  issue.isPending && issue.variables === item.id
                                }
                                onClick={() =>
                                  issue.mutate(item.id, {
                                    onError: (err) =>
                                      notifications.show({
                                        color: 'red',
                                        message: String(err),
                                      }),
                                    onSuccess: () =>
                                      notifications.show({
                                        color: 'teal',
                                        message: `Installment ${item.seq} invoice issued.`,
                                      }),
                                  })
                                }
                              >
                                Issue
                              </Button>
                            </span>
                          </Tooltip>
                        )
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
                {item && (
                  <Table.Tr>
                    <Table.Td />
                    <Table.Td
                      colSpan={(interactive ? 5 : 4) - (canReadPayers ? 0 : 1)}
                      pt={0}
                    >
                      <InstallmentContents
                        coveredLines={item.coveredLines}
                        note={item.note}
                        selectedLines={selectedLines}
                      />
                    </Table.Td>
                  </Table.Tr>
                )}
              </Fragment>
            );
          })}
        </Table.Tbody>
      </Table>

      {currencyTotals && (
        <Group gap="md">
          {Object.entries(currencyTotals).map(([cur, t]) => (
            <Text key={cur} size="sm">
              {fmtMoney(t.paid, cur)} paid · {fmtMoney(t.outstanding, cur)}{' '}
              outstanding
            </Text>
          ))}
        </Group>
      )}
    </Stack>
  );
}
